/**
 * Admin UI — HOA Phase 1a
 * Three-pane layout:
 *   LEFT   : associations list + create
 *   MIDDLE : units in the selected association + create
 *   RIGHT  : ledger + homeowners for the selected unit
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash2, Building2, Home, Users, Receipt, Mail, RefreshCw, Loader2, Zap, CheckCircle2, AlertCircle, FileText, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const GOLD = '#BCA480';
const DARK = '#434345';
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LEDGER_TYPES = [
  { value: 'charge', label: 'Charge (dues/fee)' },
  { value: 'payment', label: 'Payment received' },
  { value: 'credit', label: 'Credit' },
  { value: 'adjustment', label: 'Adjustment' },
];

export default function HoaAdmin() {
  const [associations, setAssociations] = useState([]);
  const [units, setUnits] = useState([]);
  const [homeowners, setHomeowners] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [selectedAssoc, setSelectedAssoc] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog open flags
  const [newAssocOpen, setNewAssocOpen] = useState(false);
  const [newUnitOpen, setNewUnitOpen] = useState(false);
  const [newHomeownerOpen, setNewHomeownerOpen] = useState(false);
  const [newLedgerOpen, setNewLedgerOpen] = useState(false);
  const [recordSaleOpen, setRecordSaleOpen] = useState(false);
  const [estoppelRequests, setEstoppelRequests] = useState([]);

  const loadEstoppels = async () => {
    try {
      const { data } = await api.get('/hoa/admin/estoppel-requests');
      setEstoppelRequests(data);
    } catch (e) { /* non-blocking */ }
  };
  const approveEstoppel = async (id) => {
    try {
      const { data } = await api.post(`/hoa/admin/estoppel-requests/${id}/approve`);
      toast.success('Signed and emailed to the title company.');
      await loadEstoppels();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve.');
    }
  };
  useEffect(() => { loadEstoppels(); }, []);

  const recordSale = async (form) => {
    try {
      const { data } = await api.post('/hoa/admin/units/record-sale', {
        unit_id: selectedUnit.id,
        new_owner_name: form.new_owner_name,
        sale_date: form.sale_date,
        transfer_fee_amount: form.transfer_fee_amount !== '' ? Number(form.transfer_fee_amount) : undefined,
        working_capital_amount: form.working_capital_amount !== '' ? Number(form.working_capital_amount) : undefined,
      });
      toast.success(`Sale recorded. New balance: $${data.new_balance.toFixed(2)}`);
      setRecordSaleOpen(false);
      await Promise.all([loadUnits(selectedAssoc.id), loadUnitDetail(selectedUnit.id)]);
      const refreshed = (await api.get(`/hoa/admin/units/${selectedUnit.id}`)).data;
      setSelectedUnit(refreshed);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record sale.');
    }
  };

  const seedDemoCommunity = async () => {
    try {
      const { data } = await api.post('/hoa/admin/seed-demo-community');
      toast.success(data.seeded ? 'Demo community created.' : 'Demo already exists.');
      await loadAssociations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed demo.');
    }
  };

  const sendDigestPreview = async () => {
    try {
      await api.post('/hoa/admin/analytics/send-weekly-digest-now');
      toast.success("Digest sent to the office recipients. Check your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send digest.');
    }
  };

  // --- Loaders ---
  const loadAssociations = async () => {
    const { data } = await api.get('/hoa/admin/associations');
    setAssociations(data);
    return data;
  };
  const loadUnits = async (assocId) => {
    if (!assocId) return setUnits([]);
    const { data } = await api.get(`/hoa/admin/units?association_id=${assocId}`);
    setUnits(data);
  };
  const loadUnitDetail = async (unitId) => {
    if (!unitId) { setLedger([]); setHomeowners([]); return; }
    const [{ data: ledgerData }, { data: hoData }] = await Promise.all([
      api.get(`/hoa/admin/units/${unitId}/ledger`),
      api.get(`/hoa/admin/homeowners?unit_id=${unitId}`),
    ]);
    setLedger(ledgerData);
    setHomeowners(hoData);
  };

  useEffect(() => { loadAssociations().finally(() => setLoading(false)); }, []);
  useEffect(() => { loadUnits(selectedAssoc?.id); setSelectedUnit(null); }, [selectedAssoc]);
  useEffect(() => { loadUnitDetail(selectedUnit?.id); }, [selectedUnit]);

  // --- Create handlers ---
  const createAssociation = async (form) => {
    try {
      await api.post('/hoa/admin/associations', { ...form, monthly_dues_default: Number(form.monthly_dues_default) || 0 });
      toast.success('Association created.');
      setNewAssocOpen(false);
      await loadAssociations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // --- Stripe Connect ---
  const [stripeStatus, setStripeStatus] = useState(null);
  const loadStripeStatus = async (assocId) => {
    if (!assocId) { setStripeStatus(null); return; }
    try {
      const { data } = await api.get(`/hoa/admin/associations/${assocId}/stripe/status`);
      setStripeStatus(data);
    } catch (e) {
      setStripeStatus(null);
    }
  };
  const connectStripe = async () => {
    if (!selectedAssoc) return;
    try {
      const { data } = await api.post(`/hoa/admin/associations/${selectedAssoc.id}/stripe/onboarding-link`);
      // Open onboarding in a new tab so admin keeps their place
      window.open(data.onboarding_url, '_blank', 'noopener,noreferrer');
      toast.success('Opened Stripe onboarding in a new tab.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start Stripe onboarding.');
    }
  };
  useEffect(() => { loadStripeStatus(selectedAssoc?.id); }, [selectedAssoc]);
  const createUnit = async (form) => {
    try {
      await api.post('/hoa/admin/units', {
        association_id: selectedAssoc.id,
        unit_number: form.unit_number,
        address: form.address,
        owner_name: form.owner_name,
        monthly_dues: form.monthly_dues ? Number(form.monthly_dues) : undefined,
        opening_balance: Number(form.opening_balance) || 0,
      });
      toast.success('Unit added.');
      setNewUnitOpen(false);
      await loadUnits(selectedAssoc.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const inviteHomeowner = async (form) => {
    try {
      await api.post('/hoa/admin/homeowners', { ...form, unit_id: selectedUnit.id, send_welcome_email: true });
      toast.success('Invite sent.');
      setNewHomeownerOpen(false);
      await loadUnitDetail(selectedUnit.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const resendInvite = async (homeownerId) => {
    try { await api.post(`/hoa/admin/homeowners/${homeownerId}/resend-invite`); toast.success('Invite resent.'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const addLedgerEntry = async (form) => {
    try {
      await api.post('/hoa/admin/ledger-entries', { ...form, unit_id: selectedUnit.id, amount: Number(form.amount) });
      toast.success('Entry added.');
      setNewLedgerOpen(false);
      await Promise.all([loadUnits(selectedAssoc.id), loadUnitDetail(selectedUnit.id)]);
      // Keep selected unit synced
      const refreshed = (await api.get(`/hoa/admin/units/${selectedUnit.id}`)).data;
      setSelectedUnit(refreshed);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteAssoc = async (a) => {
    if (!window.confirm(`Delete ${a.name}?`)) return;
    try { await api.delete(`/hoa/admin/associations/${a.id}`); toast.success('Deleted.'); await loadAssociations(); if (selectedAssoc?.id === a.id) setSelectedAssoc(null); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteUnit = async (u) => {
    if (!window.confirm(`Delete unit ${u.address}? Its ledger will also be removed.`)) return;
    try { await api.delete(`/hoa/admin/units/${u.id}`); toast.success('Deleted.'); await loadUnits(selectedAssoc.id); if (selectedUnit?.id === u.id) setSelectedUnit(null); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteHomeowner = async (h) => {
    if (!window.confirm(`Delete the account for ${h.email}?`)) return;
    try { await api.delete(`/hoa/admin/homeowners/${h.id}`); toast.success('Deleted.'); await loadUnitDetail(selectedUnit.id); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: GOLD }} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="hoa-admin-tab">
      <div className="flex flex-wrap items-center gap-2 justify-end">
        <Button size="sm" variant="outline" onClick={seedDemoCommunity} data-testid="hoa-seed-demo-btn">
          <Plus className="h-3 w-3 mr-1" /> Seed Demo Community
        </Button>
        <Button size="sm" variant="outline" onClick={sendDigestPreview} data-testid="hoa-digest-preview-btn">
          <Mail className="h-3 w-3 mr-1" /> Send Weekly Digest Now
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* ---------- LEFT: Associations ---------- */}
        <aside className="lg:col-span-3 bg-white rounded-lg border border-[#e6dec7] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-[#e6dec7]">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4" style={{ color: GOLD }} />
              <h3 className="font-bold text-sm" style={{ color: DARK }}>Associations</h3>
            </div>
            <Button size="sm" onClick={() => setNewAssocOpen(true)} className="text-white h-7" style={{ backgroundColor: GOLD }} data-testid="hoa-new-assoc-btn">
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          {associations.length === 0 && <div className="p-6 text-sm text-gray-500 text-center">No associations yet.</div>}
          <ul>
            {associations.map((a) => (
              <li key={a.id}>
                <button
                  onClick={() => setSelectedAssoc(a)}
                  className={`w-full text-left px-4 py-3 border-b border-[#e6dec7]/50 hover:bg-[#faf8f4] transition-colors ${selectedAssoc?.id === a.id ? 'bg-[#BCA480]/10' : ''}`}
                  data-testid={`hoa-assoc-${a.id}`}
                >
                  <div className="font-semibold text-sm" style={{ color: DARK }}>{a.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{a.city}, {a.state} · {a.unit_count} unit{a.unit_count !== 1 ? 's' : ''}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* ---------- MIDDLE: Units ---------- */}
        <section className="lg:col-span-4 bg-white rounded-lg border border-[#e6dec7] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-[#e6dec7]">
            <div className="flex items-center gap-2">
              <Home className="h-4 w-4" style={{ color: GOLD }} />
              <h3 className="font-bold text-sm" style={{ color: DARK }}>{selectedAssoc ? `Units — ${selectedAssoc.name}` : 'Select an association'}</h3>
            </div>
            {selectedAssoc && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => deleteAssoc(selectedAssoc)} className="h-7 text-red-600" data-testid="hoa-del-assoc-btn">
                  <Trash2 className="h-3 w-3" />
                </Button>
                <Button size="sm" onClick={() => setNewUnitOpen(true)} className="text-white h-7" style={{ backgroundColor: GOLD }} data-testid="hoa-new-unit-btn">
                  <Plus className="h-3 w-3" /> Unit
                </Button>
              </div>
            )}
          </div>
          {!selectedAssoc && <div className="p-10 text-sm text-gray-500 text-center">← Pick an association on the left</div>}
          {selectedAssoc && units.length === 0 && <div className="p-6 text-sm text-gray-500 text-center">No units yet.</div>}
          <ul>
            {units.map((u) => (
              <li key={u.id}>
                <button
                  onClick={() => setSelectedUnit(u)}
                  className={`w-full text-left px-4 py-3 border-b border-[#e6dec7]/50 hover:bg-[#faf8f4] transition-colors ${selectedUnit?.id === u.id ? 'bg-[#BCA480]/10' : ''}`}
                  data-testid={`hoa-unit-${u.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm" style={{ color: DARK }}>{u.unit_number ? `Unit ${u.unit_number} · ` : ''}{u.address}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{u.owner_name} · {money(u.monthly_dues)}/mo</div>
                    </div>
                    <div className={`text-sm font-mono font-bold ${u.current_balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {money(u.current_balance)}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* ---------- RIGHT: Unit detail ---------- */}
        <section className="lg:col-span-5 space-y-6">
          {!selectedUnit && (
            <div className="bg-white rounded-lg border border-[#e6dec7] p-10 text-center text-sm text-gray-500">
              ← Pick a unit to view its homeowners and ledger
            </div>
          )}
          {selectedUnit && (
            <>
              {/* Stripe Connect status for the current association */}
              <div className="bg-white rounded-lg border border-[#e6dec7] p-5" data-testid="hoa-stripe-card">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="h-4 w-4" style={{ color: GOLD }} />
                      <h3 className="font-bold text-sm" style={{ color: DARK }}>Online Payments · Stripe</h3>
                    </div>
                    <p className="text-xs text-gray-600">
                      {stripeStatus?.charges_enabled ? (
                        <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-3 w-3" /> Active — dues route to this association's bank account</span>
                      ) : stripeStatus?.account_id ? (
                        <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle className="h-3 w-3" /> Onboarding incomplete — treasurer must finish Stripe setup</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-600"><AlertCircle className="h-3 w-3" /> Not connected — dues temporarily land in the Bespoke platform account</span>
                      )}
                    </p>
                  </div>
                  <Button size="sm" onClick={connectStripe} className="text-white" style={{ backgroundColor: GOLD }} data-testid="hoa-connect-stripe-btn">
                    {stripeStatus?.account_id ? 'Resume Onboarding' : 'Connect Stripe'}
                  </Button>
                </div>
                {stripeStatus?.account_id && !stripeStatus.charges_enabled && stripeStatus.requirements_due?.length > 0 && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    <strong>Still needed:</strong> {stripeStatus.requirements_due.join(', ')}
                  </div>
                )}
              </div>

              {/* Unit header */}
              <div className="bg-white rounded-lg border border-[#e6dec7] p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] tracking-widest uppercase font-bold text-gray-500 mb-1">Selected Unit</div>
                    <h3 className="text-xl font-bold" style={{ color: DARK }}>{selectedUnit.unit_number ? `Unit ${selectedUnit.unit_number} · ` : ''}{selectedUnit.address}</h3>
                    <div className="text-sm text-gray-600 mt-1">
                      {selectedUnit.owner_name} · Monthly dues {money(selectedUnit.monthly_dues)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] tracking-widest uppercase font-bold text-gray-500 mb-1">Balance</div>
                    <div className={`text-2xl font-bold font-mono ${selectedUnit.current_balance > 0 ? 'text-orange-600' : 'text-green-600'}`} data-testid="hoa-admin-balance">
                      {money(selectedUnit.current_balance)}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => setRecordSaleOpen(true)} className="text-white" style={{ backgroundColor: GOLD }} data-testid="hoa-record-sale-btn">
                    <ArrowRightLeft className="h-3 w-3 mr-1" /> Record Sale
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteUnit(selectedUnit)} className="text-red-600" data-testid="hoa-del-unit-btn">
                    <Trash2 className="h-3 w-3 mr-1" /> Delete Unit
                  </Button>
                </div>
              </div>

              {/* Homeowners */}
              <div className="bg-white rounded-lg border border-[#e6dec7] overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-[#e6dec7]">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" style={{ color: GOLD }} />
                    <h3 className="font-bold text-sm" style={{ color: DARK }}>Homeowner Accounts</h3>
                  </div>
                  <Button size="sm" onClick={() => setNewHomeownerOpen(true)} className="text-white h-7" style={{ backgroundColor: GOLD }} data-testid="hoa-invite-ho-btn">
                    <Plus className="h-3 w-3 mr-1" /> Invite
                  </Button>
                </div>
                {homeowners.length === 0 ? (
                  <div className="p-5 text-sm text-gray-500 text-center">No homeowner accounts yet.</div>
                ) : (
                  <ul>
                    {homeowners.map((h) => (
                      <li key={h.id} className="px-4 py-3 border-b border-[#e6dec7]/50 last:border-b-0 flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-sm" style={{ color: DARK }}>{h.name}</div>
                          <div className="text-xs text-gray-500">{h.email} · {h.has_password ? 'Password set' : 'Invite pending'} · Last login: {h.last_login ? new Date(h.last_login).toLocaleDateString() : 'never'}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-7" onClick={() => resendInvite(h.id)} data-testid={`hoa-resend-${h.id}`}><RefreshCw className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => deleteHomeowner(h)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Ledger */}
              <div className="bg-white rounded-lg border border-[#e6dec7] overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-[#e6dec7]">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4" style={{ color: GOLD }} />
                    <h3 className="font-bold text-sm" style={{ color: DARK }}>Ledger · {ledger.length} entries</h3>
                  </div>
                  <Button size="sm" onClick={() => setNewLedgerOpen(true)} className="text-white h-7" style={{ backgroundColor: GOLD }} data-testid="hoa-new-ledger-btn">
                    <Plus className="h-3 w-3 mr-1" /> Entry
                  </Button>
                </div>
                {ledger.length === 0 ? (
                  <div className="p-5 text-sm text-gray-500 text-center">No ledger activity yet.</div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm" data-testid="hoa-admin-ledger">
                      <thead className="bg-[#faf8f4] text-left sticky top-0">
                        <tr>
                          <th className="px-4 py-2 font-semibold text-gray-600 text-xs tracking-wider uppercase">Date</th>
                          <th className="px-4 py-2 font-semibold text-gray-600 text-xs tracking-wider uppercase">Memo</th>
                          <th className="px-4 py-2 font-semibold text-gray-600 text-xs tracking-wider uppercase">Type</th>
                          <th className="px-4 py-2 font-semibold text-gray-600 text-xs tracking-wider uppercase text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...ledger].reverse().map((e) => (
                          <tr key={e.id} className="border-t border-gray-100">
                            <td className="px-4 py-2 text-gray-600">{new Date(e.date).toLocaleDateString()}</td>
                            <td className="px-4 py-2">{e.memo}</td>
                            <td className="px-4 py-2">
                              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                                e.type === 'charge' ? 'bg-orange-100 text-orange-700' :
                                e.type === 'payment' ? 'bg-green-100 text-green-700' :
                                e.type === 'credit' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>{e.type}</span>
                            </td>
                            <td className="px-4 py-2 text-right font-mono">{money(e.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {/* ---------- ESTOPPEL REQUESTS (Phase 2) ---------- */}
      <div className="bg-white rounded-lg border border-[#e6dec7] overflow-hidden" data-testid="hoa-estoppel-panel">
        <div className="flex items-center justify-between p-4 border-b border-[#e6dec7]">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" style={{ color: GOLD }} />
            <h3 className="font-bold text-sm" style={{ color: DARK }}>Estoppel / Payoff Requests</h3>
            <span className="text-xs text-gray-500 ml-2">{estoppelRequests.length}</span>
          </div>
          <Button size="sm" variant="outline" onClick={loadEstoppels} className="h-7" data-testid="hoa-estoppel-refresh">
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
        {estoppelRequests.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 text-center">No requests yet. Title companies can submit them at <code>/title/register</code>.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hoa-estoppel-table">
              <thead className="bg-[#faf8f4]">
                <tr>
                  <th className="px-4 py-2 text-left text-xs tracking-wider uppercase font-semibold text-gray-600">Property</th>
                  <th className="px-4 py-2 text-left text-xs tracking-wider uppercase font-semibold text-gray-600">Closing</th>
                  <th className="px-4 py-2 text-left text-xs tracking-wider uppercase font-semibold text-gray-600">Buyer / Seller</th>
                  <th className="px-4 py-2 text-left text-xs tracking-wider uppercase font-semibold text-gray-600">Fee</th>
                  <th className="px-4 py-2 text-left text-xs tracking-wider uppercase font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-2 text-right text-xs tracking-wider uppercase font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {estoppelRequests.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-4 py-2">
                      <div className="font-semibold" style={{ color: DARK }}>{r.property_address}</div>
                      <div className="text-xs text-gray-500">{r.association_name || 'Unmatched'}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{r.closing_date}{r.rush ? <span className="ml-2 text-[10px] font-bold text-red-600">RUSH</span> : null}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{r.buyer_name}<br/><span className="text-gray-500">← {r.seller_name}</span></td>
                    <td className="px-4 py-2 font-mono">{money(r.fee_amount)}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                        r.status === 'sent' ? 'bg-green-100 text-green-800' :
                        r.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                        r.status === 'pending_payment' ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-700'}`}>{r.status.replace('_',' ')}</span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {r.status === 'paid' && (
                        <Button size="sm" onClick={() => approveEstoppel(r.id)} className="h-7 text-white" style={{ backgroundColor: GOLD }} data-testid={`hoa-approve-${r.id}`}>
                          Sign & Send
                        </Button>
                      )}
                      {r.pdf_url && (
                        <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold ml-2" style={{ color: GOLD }}>
                          PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Dialogs --- */}
      <AssocDialog open={newAssocOpen} onOpenChange={setNewAssocOpen} onSubmit={createAssociation} />
      <UnitDialog open={newUnitOpen} onOpenChange={setNewUnitOpen} onSubmit={createUnit} assocDefault={selectedAssoc?.monthly_dues_default} />
      <HomeownerDialog open={newHomeownerOpen} onOpenChange={setNewHomeownerOpen} onSubmit={inviteHomeowner} />
      <LedgerDialog open={newLedgerOpen} onOpenChange={setNewLedgerOpen} onSubmit={addLedgerEntry} unit={selectedUnit} />
      <RecordSaleDialog open={recordSaleOpen} onOpenChange={setRecordSaleOpen} onSubmit={recordSale} unit={selectedUnit} association={selectedAssoc} />
    </div>
  );
}

const RecordSaleDialog = ({ open, onOpenChange, onSubmit, unit, association }) => {
  const [f, setF] = useState({ new_owner_name: '', sale_date: '', transfer_fee_amount: '', working_capital_amount: '' });
  const upd = (k, v) => setF({ ...f, [k]: v });
  const defaultTransfer = association?.transfer_fee_amount ?? '';
  const defaultWC = association?.working_capital_amount ?? '';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record a Sale</DialogTitle>
          <DialogDescription>
            Updates the owner on {unit?.address || 'this unit'} and auto-charges the transfer fee + working-capital contribution.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3 text-sm">
          <div><Label>New Owner Name</Label><Input required value={f.new_owner_name} onChange={(e) => upd('new_owner_name', e.target.value)} data-testid="hoa-sale-owner" /></div>
          <div><Label>Sale / Closing Date</Label><Input required type="date" value={f.sale_date} onChange={(e) => upd('sale_date', e.target.value)} data-testid="hoa-sale-date" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Transfer Fee ($) <span className="text-xs text-gray-400">def. {defaultTransfer || 0}</span></Label>
              <Input type="number" step="0.01" placeholder={`Default: ${defaultTransfer || 0}`} value={f.transfer_fee_amount} onChange={(e) => upd('transfer_fee_amount', e.target.value)} data-testid="hoa-sale-transfer-fee" />
            </div>
            <div>
              <Label>Working Capital ($) <span className="text-xs text-gray-400">def. {defaultWC || 0}</span></Label>
              <Input type="number" step="0.01" placeholder={`Default: ${defaultWC || 0}`} value={f.working_capital_amount} onChange={(e) => upd('working_capital_amount', e.target.value)} data-testid="hoa-sale-wc" />
            </div>
          </div>
          <p className="text-xs text-gray-500">Leave fees blank to use the association's defaults.</p>
          <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} data-testid="hoa-save-sale">Record Sale & Charge Fees</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Dialog Components ---

const AssocDialog = ({ open, onOpenChange, onSubmit }) => {
  const [f, setF] = useState({ name: '', address: '', city: '', state: 'TN', zip_code: '', association_type: 'HOA', monthly_dues_default: '0', late_fee_amount: '25', late_fee_grace_days: 10, transfer_fee_amount: '0', working_capital_amount: '0', estoppel_fee_amount: '200' });
  const upd = (k, v) => setF({ ...f, [k]: v });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Association</DialogTitle><DialogDescription>Add a community to manage.</DialogDescription></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, transfer_fee_amount: Number(f.transfer_fee_amount)||0, working_capital_amount: Number(f.working_capital_amount)||0, estoppel_fee_amount: Number(f.estoppel_fee_amount)||200 }); }} className="space-y-3 text-sm">
          <div><Label>Name</Label><Input required value={f.name} onChange={(e) => upd('name', e.target.value)} data-testid="hoa-assoc-name" /></div>
          <div><Label>Street Address</Label><Input required value={f.address} onChange={(e) => upd('address', e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>City</Label><Input required value={f.city} onChange={(e) => upd('city', e.target.value)} /></div>
            <div><Label>State</Label><Input required value={f.state} onChange={(e) => upd('state', e.target.value)} /></div>
            <div><Label>ZIP</Label><Input required value={f.zip_code} onChange={(e) => upd('zip_code', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={f.association_type} onValueChange={(v) => upd('association_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="HOA">HOA</SelectItem>
                  <SelectItem value="Condo">Condo</SelectItem>
                  <SelectItem value="POA">POA</SelectItem>
                  <SelectItem value="Master">Master</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Default Monthly Dues ($)</Label><Input type="number" step="0.01" value={f.monthly_dues_default} onChange={(e) => upd('monthly_dues_default', e.target.value)} /></div>
          </div>
          <div className="pt-3 border-t border-gray-100">
            <h4 className="text-xs tracking-wider uppercase font-bold text-gray-500 mb-2">Fee Schedule (Phase 2 & 3)</h4>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Transfer Fee ($)</Label><Input type="number" step="0.01" value={f.transfer_fee_amount} onChange={(e) => upd('transfer_fee_amount', e.target.value)} data-testid="hoa-assoc-transfer-fee" /></div>
              <div><Label>Working Capital ($)</Label><Input type="number" step="0.01" value={f.working_capital_amount} onChange={(e) => upd('working_capital_amount', e.target.value)} data-testid="hoa-assoc-wc" /></div>
              <div><Label>Estoppel Fee ($)</Label><Input type="number" step="0.01" value={f.estoppel_fee_amount} onChange={(e) => upd('estoppel_fee_amount', e.target.value)} data-testid="hoa-assoc-estoppel" /></div>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">Charged automatically on sale (transfer / WC) or at request (estoppel). All editable per unit later.</p>
          </div>
          <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} data-testid="hoa-save-assoc">Save Association</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UnitDialog = ({ open, onOpenChange, onSubmit, assocDefault }) => {
  const [f, setF] = useState({ unit_number: '', address: '', owner_name: '', monthly_dues: '', opening_balance: '0' });
  const upd = (k, v) => setF({ ...f, [k]: v });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add Unit</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Unit #</Label><Input value={f.unit_number} onChange={(e) => upd('unit_number', e.target.value)} data-testid="hoa-unit-number" /></div>
            <div className="col-span-2"><Label>Address</Label><Input required value={f.address} onChange={(e) => upd('address', e.target.value)} data-testid="hoa-unit-address" /></div>
          </div>
          <div><Label>Owner Name</Label><Input required value={f.owner_name} onChange={(e) => upd('owner_name', e.target.value)} data-testid="hoa-unit-owner" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Monthly Dues ($) <span className="text-xs text-gray-400">def. {money(assocDefault || 0)}</span></Label><Input type="number" step="0.01" value={f.monthly_dues} onChange={(e) => upd('monthly_dues', e.target.value)} /></div>
            <div><Label>Opening Balance ($)</Label><Input type="number" step="0.01" value={f.opening_balance} onChange={(e) => upd('opening_balance', e.target.value)} /></div>
          </div>
          <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} data-testid="hoa-save-unit">Save Unit</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const HomeownerDialog = ({ open, onOpenChange, onSubmit }) => {
  const [f, setF] = useState({ name: '', email: '' });
  const upd = (k, v) => setF({ ...f, [k]: v });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Homeowner</DialogTitle>
          <DialogDescription>They'll receive an email with a 72-hour link to set a password and sign in.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3 text-sm">
          <div><Label>Full Name</Label><Input required value={f.name} onChange={(e) => upd('name', e.target.value)} data-testid="hoa-ho-name" /></div>
          <div><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => upd('email', e.target.value)} data-testid="hoa-ho-email" /></div>
          <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} data-testid="hoa-save-ho"><Mail className="h-4 w-4 mr-2" /> Send Invite</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LedgerDialog = ({ open, onOpenChange, onSubmit, unit }) => {
  const [f, setF] = useState({ type: 'charge', amount: '', memo: '', date: '' });
  const upd = (k, v) => setF({ ...f, [k]: v });
  const applyMonthlyDuesPreset = () => {
    if (!unit) return;
    const month = new Date().toLocaleString(undefined, { month: 'long', year: 'numeric' });
    setF((prev) => ({ ...prev, type: 'charge', amount: String(unit.monthly_dues), memo: `${month} dues` }));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add Ledger Entry</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3 text-sm">
          <Button type="button" variant="outline" onClick={applyMonthlyDuesPreset} className="w-full h-8 text-xs" data-testid="hoa-ledger-preset">
            Preset: Charge this month's dues ({unit ? money(unit.monthly_dues) : '-'})
          </Button>
          <div>
            <Label>Type</Label>
            <Select value={f.type} onValueChange={(v) => upd('type', v)}>
              <SelectTrigger data-testid="hoa-ledger-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEDGER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Amount ($)</Label><Input required type="number" step="0.01" value={f.amount} onChange={(e) => upd('amount', e.target.value)} data-testid="hoa-ledger-amount" /></div>
          <div><Label>Memo</Label><Input required value={f.memo} onChange={(e) => upd('memo', e.target.value)} placeholder="e.g. October 2026 dues" data-testid="hoa-ledger-memo" /></div>
          <Button type="submit" className="w-full text-white" style={{ backgroundColor: GOLD }} data-testid="hoa-save-ledger">Save Entry</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
